// "Is this room's bare eigensolve warm?" -- one memo per geometry, shared by
// every job that computes it.
//
// The voxel eigensolve depends on geometry alone, and EVERY voxel job that
// needs room modes computes it on a miss and writes the shared disk cache
// (backend/voxel_cache.py): modal_warm, solve_*, evaluate, analyze_scene and
// the chart kinds. Hooks that need modes (useAutoSim, useModalViz) await the
// entry for the current geometry first, so they start on a warm cache instead
// of queueing behind the eigensolve in a worker slot of their own.
//
// Two ways an entry gets filled:
//   - `preWarmEigensolve` submits a dedicated modal_warm job. Right for the
//     moments nothing else is about to compute modes (the furniture step's
//     "Set up speakers"), so the work happens while the user is still editing.
//   - `warmEigensolveVia` adopts a job that is about to compute the
//     eigensolve anyway. A placement solve used to be submitted TOGETHER with
//     a modal_warm for the same room (2026-09-12: two POSTs in the same
//     instant); the backend's cache lock made one wait on the other, so the
//     modal_warm bought nothing and cost a worker slot, a CPU reservation,
//     its own enqueue transaction and a poll loop. Adopting the solve gives
//     the waiting hooks the same guarantee for free.
//
// Invariants: one entry per signature; a new geometry clears entries for the
// old one (their jobs keep running server-side and still fill the cache); a
// successful entry is KEPT as the "already warm" marker; a failed one is
// dropped so the next caller can warm again. Entry promises never reject.

import { geometrySignature } from '@roomtreatment/acoustics-client';
import type { EditorDoc } from '../components/room/state/editorState';
import type { ModalPipeline } from './adminTypes';

interface WarmEntry {
  signature: string;
  promise: Promise<void>;
}

const entries = new Map<string, WarmEntry>();

/** The in-flight or completed warm for `signature`, if any. */
export function eigensolveWarmFor(signature: string): Promise<void> | undefined {
  return entries.get(signature)?.promise;
}

/** Record `work` as the warm for `signature`. Resolves when `work` settles,
 *  either way; `onFailure` sees the error before the entry is dropped. */
export function trackEigensolveWarm(
  signature: string,
  work: Promise<unknown>,
  onFailure?: (err: unknown) => void,
): Promise<void> {
  if (entries.size > 0 && !entries.has(signature)) {
    console.log(
      '[eigensolveWarm] new geometry requested; abandoning %d stale warm(s).',
      entries.size,
    );
    entries.clear();
  }
  const entry: WarmEntry = { signature, promise: Promise.resolve() };
  entry.promise = work.then(
    () => undefined,
    (err: unknown) => {
      onFailure?.(err);
      if (entries.get(signature) === entry) entries.delete(signature);
    },
  );
  entries.set(signature, entry);
  return entry.promise;
}

/** Let a job that computes the bare eigensolve as part of its own work (any
 *  voxel solve_* / evaluate job) stand in for a modal_warm on `doc`'s
 *  geometry. Call it with the job's promise right after submitting, INSTEAD
 *  of `preWarmEigensolve`. A warm that already exists for this geometry is
 *  kept -- it is at least as far along. No-op off the voxel pipeline. */
export function warmEigensolveVia(
  doc: EditorDoc,
  pipeline: ModalPipeline | undefined,
  job: Promise<unknown>,
): void {
  if (pipeline !== 'voxel') return;
  const signature = geometrySignature(doc);
  if (entries.has(signature)) return;
  void trackEigensolveWarm(signature, job);
}

/** Forget every warm. Use on doc reset / sign-out. */
export function clearEigensolveWarm(): void {
  entries.clear();
}
